import { Component } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import {
  WithSubscriptionComponent,
  EnterpriseSearchService,
  ServiceType,
  UserService
} from '@sabstravtech/obtservices/angular';
import { LocationTypes } from '@sabstravtech/obtservices/base';
import moment from 'moment';

import { ThemeService } from '../../services/theme.service';

/**
 * Base component for all transport results pages.
 * Provides shared functionality for accessibility, title management, and common UI patterns.
 *
 * Extend this class for: Cars, Hotels, Rail, Taxis, Eurostar, etc.
 */
@Component({
  template: '',
  standalone: false
})
export abstract class BaseResultsComponent extends WithSubscriptionComponent {
  // Common UI state
  showSortOptions = false;
  showFilterOptions = false;
  canOverride = false;

  // Accessibility - tracks hidden elements for aria management
  protected hiddenElements: Element[] = [];

  constructor(
    protected titleService: Title,
    protected translateService: TranslateService,
    protected themeService: ThemeService,
    protected baseSearchService: EnterpriseSearchService,
    protected baseUserService: UserService,
    protected baseRouter: Router
  ) {
    super();
  }

  /**
   * Sets the page title with translation and theme suffix
   * @param titleKey - Translation key for the title (e.g., 'Car hire results')
   */
  protected setResultsTitle(titleKey: string): void {
    const suffix = this.themeService.isLightningEnv ? ' - LightUk' : '';
    const translatedTitle = this.translateService.instant(titleKey);
    this.titleService.setTitle(`${translatedTitle}${suffix}`);
  }

  /**
   * Initialises common state - call from ngOnInit in child components
   */
  protected initBaseResults(): void {
    this.canOverride = this.baseUserService.canOverride();
  }

  // ============ Toggle Methods ============

  /**
   * Toggles sort options dropdown and closes filter options
   */
  toggleSortOptions(): void {
    this.showSortOptions = !this.showSortOptions;
    this.showFilterOptions = false;
  }

  /**
   * Toggles filter options panel and closes sort options
   */
  toggleFilterOptions(): void {
    this.showFilterOptions = !this.showFilterOptions;
    this.showSortOptions = false;
  }

  /**
   * Closes the filter options panel
   */
  closeFilterOptions(): void {
    this.showFilterOptions = false;
  }

  // ============ Accessibility Methods ============

  /**
   * Moves focus to the next sibling element (for arrow key navigation)
   */
  moveArrowDown(e: Event): void {
    e.preventDefault();
    const nextElement = document.activeElement?.nextElementSibling as HTMLElement;
    if (nextElement) {
      nextElement.focus();
    }
  }

  /**
   * Moves focus to the previous sibling element (for arrow key navigation)
   */
  moveArrowUp(e: Event): void {
    e.preventDefault();
    const prevElement = document.activeElement?.previousElementSibling as HTMLElement;
    if (prevElement) {
      prevElement.focus();
    }
  }

  /**
   * Skips focus to the first focusable element within a container
   * @param containerId - The ID of the container element
   * @returns false to prevent default behaviour
   */
  protected skipToResultContent(containerId: string): boolean {
    const container = document.getElementById(containerId);
    if (container) {
      const focusable = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length > 0) {
        (focusable[0] as HTMLElement).focus();
      }
    }
    return false;
  }

  /**
   * Traps focus within a container (for modals/dialogs)
   * @param event - The keyboard event
   * @param index - Current index in the focusable elements
   * @param array - Array of items being navigated
   * @param containerId - ID of the container to trap focus within
   */
  protected trapFocus(
    event: KeyboardEvent,
    index: number,
    array: unknown[],
    containerId: string
  ): void {
    if (index === array.length - 1) {
      event.preventDefault();
      const container = document.getElementById(containerId);
      if (container) {
        const focusable = container.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length > 0) {
          (focusable[0] as HTMLElement).focus();
        }
      }
    }
  }

  /**
   * Traps focus in reverse direction
   * @param event - The keyboard event
   * @param containerId - ID of the container
   */
  protected trapFocusReverse(event: KeyboardEvent, containerId: string): void {
    event.preventDefault();
    const container = document.getElementById(containerId);
    if (container) {
      const focusable = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length > 0) {
        (focusable[focusable.length - 1] as HTMLElement).focus();
      }
    }
  }

  /**
   * Toggles aria-hidden on background elements when a modal/panel is open
   * @param wrapperId - ID of the wrapper element that should remain visible
   * @param isOpen - Whether the panel is open
   */
  protected ariaToggleHideBackground(wrapperId: string, isOpen: boolean): void {
    if (isOpen) {
      this.hiddenElements = [];
      const selection = document.querySelectorAll('*');
      const wrapper = document.getElementById(wrapperId);

      if (!wrapper) {
        return;
      }

      selection.forEach((element) => {
        if (
          element !== wrapper &&
          !wrapper.contains(element) &&
          !element.contains(wrapper) &&
          (!element.getAttribute('aria-hidden') || element.getAttribute('aria-hidden') === 'false')
        ) {
          element.setAttribute('aria-hidden', 'true');
          this.hiddenElements.push(element);
        }
      });
    } else {
      this.hiddenElements.forEach((element) => {
        element.setAttribute('aria-hidden', 'false');
      });
      this.hiddenElements = [];
    }
  }

  /**
   * Ensures an active typeahead item is scrolled into view
   */
  ensureElementIsScrolledTo(event: Event): void {
    try {
      const target = event.target as HTMLElement;
      const typeAheadList = target.nextElementSibling as HTMLElement;
      const activeButton = typeAheadList?.getElementsByClassName('active')[0] as HTMLElement;

      if (!typeAheadList || !activeButton) {
        return;
      }

      if (
        activeButton.offsetTop + activeButton.clientHeight >
        typeAheadList.clientHeight + typeAheadList.scrollTop
      ) {
        typeAheadList.scrollTop =
          activeButton.offsetTop + activeButton.clientHeight - typeAheadList.clientHeight;
      } else if (activeButton.offsetTop < typeAheadList.scrollTop) {
        typeAheadList.scrollTop = activeButton.offsetTop;
      }
    } catch (e) {
      console.log("Couldn't find elements to scroll");
    }
  }

  // ============ Re-search Methods ============

  /**
   * Performs a re-search for the given service type
   * @param serviceType - The service type to search
   * @param additionalSetup - Optional callback for additional setup before search
   */
  protected performReSearch(serviceType: ServiceType, additionalSetup?: () => void): void {
    this.baseSearchService.search_objects[serviceType].chosen = true;

    if (additionalSetup) {
      additionalSetup();
    }

    this.baseSearchService.determineHighestSearchPriority();
    this.baseSearchService.startSearches();
  }

  // ============ Cross-service Navigation ============

  /**
   * Navigates to hotel search with pre-populated parameters from current search
   * @param serviceType - The current service type (for backToType)
   * @param dates - Check-in and check-out dates
   * @param location - Location details for hotel search
   */
  protected navigateToHotelSearch(
    serviceType: ServiceType,
    dates: { checkin: moment.Moment; checkout: moment.Moment },
    location?: {
      name?: string;
      latitude?: number;
      longitude?: number;
      countryCode?: string;
      type?: LocationTypes;
    }
  ): void {
    const hotelSearchParams = this.baseSearchService.searches[ServiceType.Hotel];
    hotelSearchParams.backToType = serviceType;

    // Set dates
    hotelSearchParams.checkin_date = dates.checkin;
    hotelSearchParams.checkout_date = dates.checkout;

    // Set location if provided
    if (location) {
      hotelSearchParams.location_type_select = LocationTypes.Address;
      hotelSearchParams.location = {
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        countryCode: location.countryCode,
        type: location.type || LocationTypes.Address
      };
    }

    // Update search state
    this.baseSearchService.search_objects[ServiceType.Flight].chosen = false;
    this.baseSearchService.search_objects[ServiceType.Hotel].chosen = true;

    const searchObj = this.baseSearchService.init_search_objects();
    const hotelResultUrl = searchObj.HOTEL.resultUrl;
    this.baseSearchService.search_objects[ServiceType.Hotel].priority = searchObj.HOTEL.priority;
    this.baseSearchService.search_objects[ServiceType.Hotel].resultUrl = hotelResultUrl;

    this.baseRouter.navigate([hotelResultUrl]);
  }
}
